import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useSearchParams } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { getContactosComercial, getEtiquetasContactoComercial } from '../../api/client'
import PageHeader from '../../components/PageHeader'
import { puede, mensajeError, ordenarCatalogo } from '../../components/comercial/comercialLogica'
import {
  filtrosDeParams, paramsConFiltros, hayFiltros, contarFiltrosAdicionales, consultaListado, filtrarEnPagina,
  ordenarContactos, ordenVisible, columnasValidas, alternarColumna, COLUMNAS_OPCIONALES, COLUMNAS_PREDETERMINADAS,
} from '../../components/comercial/contactosLogica'
import { siguienteOrden } from '../../components/comercial/organizacionesLogica'
import { leerLocal, guardarLocal } from '../../components/comercial/comercialUtiles'
import useFichaComercial from '../../components/comercial/useFichaComercial'
import ComercialFichas from '../../components/comercial/ComercialFichas'
import ComercialVacio from '../../components/comercial/ComercialVacio'
import ComercialContactosLista from '../../components/comercial/ComercialContactosLista'
import ComercialSelectorOrganizacion from '../../components/comercial/ComercialSelectorOrganizacion'
import { BotonFiltros, MenuColumnas } from '../../components/comercial/ComercialListadoControles'

const PASO = 100                                   // rows shown at a time ("Mostrar más")
const CLAVE_COLUMNAS = 'comercial:contactos:columnas'
const control = 'border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-gray-200'

// Comercial > CRM > Contactos: the list with its search and filters, and each
// contact's card in the side panel (?contacto=<id>; a new one, ?contacto=nueva).
// Like Organizaciones: filters in the address, search and Organización on the
// bar and the rest behind "Filtros", columns chosen and kept in the browser.
// The server filters by organization (with its subordinates), label and state,
// and searches names, emails and phones; the page adds organizations and
// cargos to the search, "Sin organización", the order and PASO rows at a time.
export default function ComercialContactos() {
  const { t } = useTranslation('comercial')
  const { user } = useAuth()
  const puedeEditar = puede(user, 'comercial_editar')
  const [params, setParams] = useSearchParams()
  const location = useLocation()
  const { ficha, abrir } = useFichaComercial()
  const filtros = filtrosDeParams(params)

  const [texto, setTexto] = useState(filtros.q)     // the search box; it reaches the address after a pause
  const [columnas, setColumnas] = useState(() => leerLocal(CLAVE_COLUMNAS, columnasValidas))
  const [verFiltros, setVerFiltros] = useState(() => contarFiltrosAdicionales(filtros) > 0)
  const [orden, setOrden] = useState({ campo: 'nombre', dir: 'asc' })
  const [mas, setMas] = useState({ clave: '', n: PASO })
  const [lista, setLista] = useState(null)
  const [busqueda, setBusqueda] = useState({ clave: '', ids: null })   // ids the server's search found (other emails, phones)
  const [etiquetas, setEtiquetas] = useState([])
  const [error, setError] = useState('')
  const [version, setVersion] = useState(0)

  const estadoRef = useRef(location.state)
  useEffect(() => { estadoRef.current = location.state }, [location.state])
  const fijarFiltros = useCallback((cambio) => {
    setParams(prev => paramsConFiltros(prev, { ...filtrosDeParams(prev), ...cambio }),
      { replace: true, state: estadoRef.current })
  }, [setParams])

  useEffect(() => {
    if (texto.trim() === filtros.q) return undefined
    const pausa = setTimeout(() => fijarFiltros({ q: texto }), 300)
    return () => clearTimeout(pausa)
  }, [texto, filtros.q, fijarFiltros])

  const consulta = JSON.stringify(consultaListado(filtros))
  useEffect(() => {
    let vivo = true
    getContactosComercial(JSON.parse(consulta))
      .then(d => { if (vivo) { setLista(d); setError('') } })
      .catch(e => vivo && setError(mensajeError(e, t, t('contactos.cargaFallo'))))
    return () => { vivo = false }
  }, [consulta, version, t])
  const claveBusqueda = filtros.q ? `${consulta}|${filtros.q}|${version}` : ''
  useEffect(() => {
    if (!claveBusqueda) return undefined
    let vivo = true
    getContactosComercial({ ...JSON.parse(consulta), q: filtros.q })
      .then(d => vivo && setBusqueda({ clave: claveBusqueda, ids: new Set(d.map(c => c.id)) }))
      .catch(() => {})
    return () => { vivo = false }
  }, [claveBusqueda, consulta, filtros.q])
  const delServidor = busqueda.clave && busqueda.clave === claveBusqueda ? busqueda.ids : null

  useEffect(() => { getEtiquetasContactoComercial().then(d => setEtiquetas(ordenarCatalogo(d))).catch(() => {}) }, [])

  const ordenActual = useMemo(() => ordenVisible(orden, columnas), [orden, columnas])
  const filas = useMemo(
    () => ordenarContactos(filtrarEnPagina(lista, { q: filtros.q, sinorg: filtros.sinorg }, delServidor), ordenActual),
    [lista, filtros.q, filtros.sinorg, delServidor, ordenActual])
  const claveFilas = `${consulta}|${filtros.q}|${filtros.sinorg}`
  const mostrar = mas.clave === claveFilas ? mas.n : PASO

  const cambiarColumnas = (c) => { setColumnas(c); guardarLocal(CLAVE_COLUMNAS, c) }
  const limpiar = () => { setTexto(''); fijarFiltros({ q: '', organizacion: null, etiqueta: null, estado: 'no', sinorg: false }) }
  const conFiltros = hayFiltros({ ...filtros, q: texto })
  const adicionales = contarFiltrosAdicionales(filtros)
  const nuevo = puedeEditar && (
    <button type="button" onClick={() => abrir('contacto', 'nueva')}
      className="bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-800 inline-flex items-center gap-1.5">
      + {t('contactos.nuevo')}
    </button>
  )

  return (
    <div className="p-4 sm:p-8">
      <div className="mb-5">
        <PageHeader title={t('contactos.titulo')} actions={nuevo} />
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <input type="search" value={texto} onChange={e => setTexto(e.target.value)} aria-label={t('contactos.buscar')}
          placeholder={t('contactos.buscar')} className={`${control} w-full sm:w-72`} />
        <div className="w-full sm:w-64">
          <ComercialSelectorOrganizacion value={filtros.organizacion} onChange={v => fijarFiltros({ organizacion: v })}
            placeholder={t('contactos.todasOrganizaciones')} textoVacio={t('contactos.todasOrganizaciones')}
            aria-label={t('listaContactos.organizaciones')} />
        </div>
        <BotonFiltros abierto={verFiltros} cuenta={adicionales} onClick={() => setVerFiltros(v => !v)} controla="filtros-contactos" />
        {conFiltros && (
          <button type="button" onClick={limpiar} className="text-sm text-gray-500 underline hover:text-gray-900 px-1">
            {t('organizaciones.limpiar')}
          </button>
        )}
        <div className="ml-auto">
          <MenuColumnas columnas={columnas} onCambiar={cambiarColumnas} alternar={alternarColumna}
            fija={t('listaContactos.nombre')} opcionales={COLUMNAS_OPCIONALES} predeterminadas={COLUMNAS_PREDETERMINADAS}
            titulos={{
              organizaciones: t('listaContactos.organizaciones'), cargo: t('listaContactos.cargo'), correo: t('listaContactos.correo'),
              telefono: t('listaContactos.telefono'), etiquetas: t('listaContactos.etiquetas'),
            }} />
        </div>
      </div>

      {verFiltros && (
        <div id="filtros-contactos" className="flex flex-wrap items-center gap-2 mb-4 rounded-xl border border-gray-200 bg-gray-50 p-3">
          <select value={filtros.etiqueta || ''} onChange={e => fijarFiltros({ etiqueta: Number(e.target.value) || null })}
            aria-label={t('listaContactos.etiquetas')} className={control}>
            <option value="">{t('contactos.todasEtiquetas')}</option>
            {etiquetas.map(x => <option key={x.id} value={x.id}>{x.nombre}{x.activo ? '' : ` (${t('chips.inactivo').toLowerCase()})`}</option>)}
          </select>
          <select value={filtros.estado} onChange={e => fijarFiltros({ estado: e.target.value })}
            aria-label={t('organizaciones.estado')} className={control}>
            <option value="no">{t('contactos.activos')}</option>
            <option value="si">{t('contactos.archivados')}</option>
            <option value="todos">{t('contactos.todos')}</option>
          </select>
          <label className="inline-flex items-center gap-1.5 text-sm text-gray-600 cursor-pointer px-1">
            <input type="checkbox" checked={filtros.sinorg} onChange={e => fijarFiltros({ sinorg: e.target.checked })}
              className="rounded border-gray-300" />
            {t('contactos.sinOrganizacion')}
          </label>
        </div>
      )}

      {error && (
        <p className="mb-3 text-sm text-red-600">
          {error}{' '}
          <button type="button" onClick={() => setVersion(v => v + 1)} className="underline">{t('organizaciones.reintentar')}</button>
        </p>
      )}

      {!lista ? (!error && <p className="text-sm text-gray-400">{t('panel.cargando')}</p>)
        : !filas.length ? (
          conFiltros
            ? <ComercialVacio titulo={t('contactos.sinResultados')} texto={t('organizaciones.sinResultadosTexto')}>
                <button type="button" onClick={limpiar} className="text-sm text-gray-700 underline">{t('organizaciones.limpiar')}</button>
              </ComercialVacio>
            : <ComercialVacio titulo={t('contactos.vacio')} texto={t(puedeEditar ? 'contactos.vacioTexto' : 'contactos.vacioTextoVer')}>
                {nuevo}
              </ComercialVacio>
        ) : (
          <>
            <p className="text-xs text-gray-500 mb-2">{t('contactos.contador', { count: filas.length })}</p>
            <ComercialContactosLista filas={filas.slice(0, mostrar)} columnas={columnas} orden={ordenActual}
              onOrden={c => setOrden(o => siguienteOrden(ordenVisible(o, columnas), c))}
              actual={ficha?.tipo === 'contacto' ? ficha.id : null} onAbrir={abrir} />
            {filas.length > mostrar && (
              <div className="mt-3 text-center">
                <button type="button" onClick={() => setMas({ clave: claveFilas, n: mostrar + PASO })}
                  className="px-4 py-2 rounded-lg text-sm border border-gray-200 bg-white text-gray-700 hover:bg-gray-50">
                  {t('organizaciones.mostrarMas', { count: filas.length - mostrar })}
                </button>
              </div>
            )}
          </>
        )}

      <ComercialFichas onCambio={() => setVersion(v => v + 1)} />
    </div>
  )
}
